// React and hooks
import React, { useState, useEffect } from 'react';
// API client
import api from '../../lib/api';
// Theme hook
import useTheme from '../../hooks/useTheme';
// i18n
import { useTranslation } from 'react-i18next';

const AddProductModal = ({ isOpen, onClose, onSaved, product = null, mode = 'create' }) => {
  // States
  const [name, setName] = useState('');
  const [brandId, setBrandId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [hasBaseWeight, setHasBaseWeight] = useState(false); 
  const [fixedWeight, setFixedWeight] = useState('');
  const [busy, setBusy] = useState(false);

  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  // Hooks
  const { theme } = useTheme();
  const { t } = useTranslation();

  // Button styles
  const baseBtn =
    theme === 'dark'
      ? "inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold shadow transition duration-200 bg-white text-black hover:bg-black hover:text-white hover:scale-105 disabled:opacity-60"
      : "inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold shadow transition duration-200 bg-gray-900 text-white hover:bg-white hover:text-black hover:scale-105 disabled:opacity-60";

  // Load brands, categories, suppliers
  useEffect(() => {
    if (isOpen) {
      const fetchData = async () => {
        try {
          const [bRes, cRes, sRes] = await Promise.all([
            api.get('/brands'),
            api.get('/categories'),
            api.get('/suppliers'),
          ]);
          setBrands(Array.isArray(bRes.data) ? bRes.data : []);
          setCategories(Array.isArray(cRes.data) ? cRes.data : []);
          setSuppliers(Array.isArray(sRes.data) ? sRes.data : []);
        } catch (err) {
          console.error('Error loading brands/categories/suppliers:', err);
        }
      };
      fetchData();
    }
  }, [isOpen]);

  // Init values on edit/create
  useEffect(() => {
    if (product && mode === 'edit') {
      setName(product.name || '');
      setBrandId(product.brandId || product.brand?.id || '');
      setCategoryId(product.categoryId || product.category?.id || '');
      setSupplierId(product.supplierId || product.supplier?.id || '');
      setHasBaseWeight(!!product.hasBaseWeight);
      setFixedWeight(product.fixedWeight ? String(product.fixedWeight) : '');
    } else if (mode === 'create') {
      setName('');
      setBrandId('');
      setCategoryId('');
      setSupplierId('');
      setHasBaseWeight(false);
      setFixedWeight('');
    }
  }, [product, mode, isOpen]);

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !categoryId.trim() || !brandId.trim() || !supplierId.trim()) return;

    try {
      setBusy(true);

      const authRaw = localStorage.getItem('auth') || '{}';
      const auth = JSON.parse(authRaw);
      const userId = auth?.userId;

      const payload = {
        name,
        brandId,
        categoryId,
        supplierId,
        hasBaseWeight,
        fixedWeight: hasBaseWeight && fixedWeight ? parseFloat(fixedWeight) : null,
        userId,
      };

      let res;
      if (mode === 'create') {
        res = await api.post('/product-templates', payload);
      } else if (mode === 'edit') {
        const id = product?.id;
        if (!id) throw new Error("Product template ID missing for update");
        res = await api.put(`/product-templates/${id}`, payload);
      }

      if (onSaved) onSaved(res.data);
      onClose();
    } catch (error) {
      console.error(error);
      alert(t('ErrorSaveProductTemplate') || 'Failed To Save Product Template');
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen) return null;

  const getId = (obj) => obj?.id;

  // Render
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-sm shadow-lg space-y-4">
        <h2 className="text-lg font-bold text-gray-800 dark:text-white">
          {mode === 'create' ? t('AddProductTemplate') : t('EditProductTemplate')}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Product name */}
          <input
            type="text"
            placeholder={t('ProductName')}
            className="w-full border border-gray-300 rounded-md px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            disabled={busy}
          />

          {/* Brand */}
          <select
            value={brandId}
            onChange={(e) => setBrandId(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            required
            disabled={busy}
          >
            <option value="">{t('SelectBrand')}</option>
            {brands.map((b) => (
              <option key={getId(b)} value={getId(b)}>
                {b.name}
              </option>
            ))}
          </select>

          {/* Category */}
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            required
            disabled={busy}
          >
            <option value="">{t('SelectCategory')}</option>
            {categories.map((c) => (
              <option key={getId(c)} value={getId(c)}>
                {c.name}
              </option>
            ))}
          </select>

          {/* Supplier */}
          <select
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            required
            disabled={busy}
          >
            <option value="">{t('SelectSupplier')}</option>
            {suppliers.map((s) => (
              <option key={getId(s)} value={getId(s)}>
                {s.name}
              </option>
            ))}
          </select>

          {/* Checkbox fixed weight */}
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={hasBaseWeight}
              onChange={(e) => setHasBaseWeight(e.target.checked)}
              disabled={busy}
            />
            <span className="text-gray-700 dark:text-gray-200">{t('FixedWeightProduct')}</span>
          </label>

          {/* Fixed weight input */}
          {hasBaseWeight && (
            <input
              type="number"
              step="0.01"
              placeholder={t('FixedWeight')}
              className="w-full border border-gray-300 rounded-md px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              value={fixedWeight}
              onChange={(e) => setFixedWeight(e.target.value)}
              required
              disabled={busy}
            />
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className={baseBtn} disabled={busy}>
              {t('Cancel')}
            </button>
            <button type="submit" className={baseBtn} disabled={busy}>
              {busy ? t('Saving') : t('Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProductModal;